import { supabase } from '../lib/supabase';
import { createRequestCache } from '../lib/requestCache';
import { BugReport, BugStatus, BugSeverity } from '../types/bugReports';

const allBugReportsCache = createRequestCache<BugReport[]>(60_000);
const openBugReportCountCache = createRequestCache<number>(60_000);

export function invalidateBugReportCaches(): void {
  allBugReportsCache.invalidate();
  openBugReportCountCache.invalidate();
}

/** Per-image binary cap before base64 (keeps DB row size reasonable) */
const MAX_EMBED_IMAGE_BYTES = 1.5 * 1024 * 1024;
const ALLOWED_IMAGE = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result;
      if (typeof result !== 'string' || !result.startsWith('data:image/')) {
        reject(new Error('Could not read image'));
        return;
      }
      resolve(result);
    };
    r.onerror = () => reject(new Error('Could not read image'));
    r.readAsDataURL(file);
  });
}

function parseBugReportRow(row: Record<string, unknown>): BugReport {
  const rawUrls = row.attachment_urls;
  let attachment_urls: string[] | undefined;
  if (Array.isArray(rawUrls)) {
    attachment_urls = rawUrls.filter((u): u is string => typeof u === 'string');
  } else if (rawUrls != null && typeof rawUrls === 'string') {
    try {
      const p = JSON.parse(rawUrls) as unknown;
      if (Array.isArray(p)) attachment_urls = p.filter((u): u is string => typeof u === 'string');
    } catch {
      /* ignore */
    }
  }

  const rawImages = row.attachment_images;
  let attachment_images: string[] | undefined;
  if (Array.isArray(rawImages)) {
    attachment_images = rawImages.filter(
      (u): u is string => typeof u === 'string' && u.startsWith('data:image/')
    );
  }

  return { ...(row as unknown as BugReport), attachment_urls, attachment_images };
}

/**
 * Encode image files as data URLs (embedded pixels, not storage links).
 * Exported standalone so callers do not depend on `bugReportService` object shape at runtime.
 */
export async function filesToAttachmentImages(files: File[]): Promise<string[]> {
  const out: string[] = [];
  for (const file of files) {
    if (!ALLOWED_IMAGE.includes(file.type)) {
      throw new Error(`Unsupported image type: ${file.type || file.name}. Use PNG, JPEG, WebP, or GIF.`);
    }
    if (file.size > MAX_EMBED_IMAGE_BYTES) {
      throw new Error(`Each image must be under 1.5 MB for embedding (${file.name})`);
    }
    out.push(await readFileAsDataUrl(file));
  }
  return out;
}

export const bugReportService = {
  filesToAttachmentImages,

  async createBugReport(data: {
    title: string;
    description: string;
    severity: BugSeverity;
    attachment_url?: string;
    attachment_urls?: string[];
    /** Inline data:image/...;base64,... strings */
    attachment_images?: string[];
  }) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const reporterId = user?.id;
    if (!reporterId) throw new Error('You must be signed in to submit a feature request');

    const hasInline = data.attachment_images && data.attachment_images.length > 0;
    const legacyUrls = data.attachment_urls?.length
      ? data.attachment_urls
      : data.attachment_url
        ? [data.attachment_url]
        : [];

    const payload = {
      title: data.title,
      description: data.description,
      severity: data.severity,
      reporter_id: reporterId,
      attachment_images: hasInline ? data.attachment_images : [],
      attachment_url: hasInline ? null : legacyUrls[0] ?? null,
      attachment_urls: hasInline ? [] : legacyUrls,
    };

    const { data: result, error } = await supabase.from('bug_reports').insert([payload]).select().single();

    if (error) throw error;
    invalidateBugReportCaches();
    return parseBugReportRow(result as Record<string, unknown>);
  },

  async getUserBugReports() {
    const { data, error } = await supabase
      .from('bug_reports')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map((row) => parseBugReportRow(row as Record<string, unknown>));
  },

  async getAllBugReports(force = false) {
    return allBugReportsCache.get(async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((row) => parseBugReportRow(row as Record<string, unknown>));
    }, { force });
  },

  /** Lightweight count for notification bell (avoids full-table fetch on every dashboard update). */
  async getOpenBugReportCount(force = false) {
    return openBugReportCountCache.get(async () => {
      const { count, error } = await supabase
        .from('bug_reports')
        .select('id', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress']);

      if (error) throw error;
      return count ?? 0;
    }, { force });
  },

  async updateBugReportStatus(id: string, status: BugStatus) {
    const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };

    if (status === 'resolved') {
      updates.resolved_at = new Date().toISOString();
    }
    if (status === 'in_progress') {
      updates.in_progress_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('bug_reports')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    invalidateBugReportCaches();
    return data;
  },

  async addResolutionNotes(id: string, notes: string) {
    const { data, error } = await supabase
      .from('bug_reports')
      .update({
        resolution_notes: notes,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    invalidateBugReportCaches();
    return data;
  },

  async assignBugReport(id: string, assignedToId: string) {
    const { data, error } = await supabase
      .from('bug_reports')
      .update({
        assigned_to_id: assignedToId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    invalidateBugReportCaches();
    return data;
  },

  async deleteBugReport(id: string) {
    const { error } = await supabase.from('bug_reports').delete().eq('id', id);

    if (error) throw error;
    invalidateBugReportCaches();
  },
};
